import { Controller } from "@hotwired/stimulus"
import { toast } from "../services/toast"

export default class extends Controller {
  static targets = [
    "table",
    "tableBody",
    "fileInput",
    "importButton",
    "progressContainer",
    "progressBar",
    "progressText",
    "errorSummary",
    "errorList",
    "rowCount",
    "validCount",
    "errorCount"
  ]

  static values = {
    columns: Array,
    uniqueField: String,
    existingValues: Array,
    importTaskId: Number,
    pollInterval: { type: Number, default: 2000 }
  }

  connect() {
    this.seenValues = new Set()
    this.existingValuesSet = new Set(this.existingValuesValue.map(v => v.toLowerCase().trim()))
    this.setupPasteHandler()
    this.addEmptyRows(5)
    this.updateCounts()
  }

  disconnect() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }
  }

  setupPasteHandler() {
    this.tableBodyTarget.addEventListener("paste", this.handlePaste.bind(this))
  }

  handlePaste(event) {
    event.preventDefault()
    
    const clipboardData = event.clipboardData || window.clipboardData
    const pastedText = clipboardData.getData("text")
    
    if (!pastedText) return
    
    // Parse pasted data (tab-separated columns, newline-separated rows)
    const rows = pastedText.split(/\r?\n/).filter(row => row.trim())
    
    if (rows.length === 0) return
    
    // Clear existing rows
    this.tableBodyTarget.innerHTML = ""
    this.seenValues.clear()
    
    // Add pasted rows
    rows.forEach(row => {
      const cells = row.split("\t")
      this.addRow(cells)
    })
    
    // Add a few empty rows at the end
    this.addEmptyRows(3)
    
    this.validateAllRows()
    this.updateCounts()
  }

  addRow(values = []) {
    const rowIndex = this.tableBodyTarget.children.length
    const tr = document.createElement("tr")
    tr.dataset.rowIndex = rowIndex
    
    // Add cells for each column
    this.columnsValue.forEach((column, colIndex) => {
      const td = document.createElement("td")
      td.contentEditable = "true"
      td.dataset.field = column.field
      td.dataset.required = column.required || false
      td.textContent = values[colIndex] || ""
      
      td.addEventListener("blur", () => this.validateRow(tr))
      td.addEventListener("input", () => this.debouncedValidate(tr))
      
      tr.appendChild(td)
    })
    
    // Add delete button
    const actionTd = document.createElement("td")
    actionTd.className = "import-table__actions"
    actionTd.innerHTML = `
      <button type="button" class="btn-icon btn-icon--danger" data-action="click->import-table#removeRow">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h18"></path>
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
        </svg>
      </button>
    `
    tr.appendChild(actionTd)
    
    this.tableBodyTarget.appendChild(tr)
    
    return tr
  }

  addEmptyRows(count) {
    for (let i = 0; i < count; i++) {
      this.addRow()
    }
  }

  addRowButton(event) {
    event.preventDefault()
    this.addRow()
    this.updateCounts()
  }

  removeRow(event) {
    event.preventDefault()
    const row = event.target.closest("tr")
    if (row) {
      row.remove()
      this.validateAllRows()
      this.updateCounts()
    }
  }

  debouncedValidate(row) {
    if (this.validateTimeout) {
      clearTimeout(this.validateTimeout)
    }
    this.validateTimeout = setTimeout(() => {
      this.validateRow(row)
      this.updateCounts()
    }, 300)
  }

  validateRow(row) {
    const cells = row.querySelectorAll("td[data-field]")
    let hasError = false
    const rowData = {}
    
    cells.forEach(cell => {
      const field = cell.dataset.field
      const value = cell.textContent.trim()
      const isRequired = cell.dataset.required === "true"
      
      rowData[field] = value
      
      // Remove previous error state
      cell.classList.remove("cell-error")
      
      // Check required
      if (isRequired && !value) {
        cell.classList.add("cell-error")
        hasError = true
      }
    })
    
    // Check for duplicates in unique field
    if (this.uniqueFieldValue && rowData[this.uniqueFieldValue]) {
      const uniqueValue = rowData[this.uniqueFieldValue].toLowerCase().trim()
      const uniqueCell = row.querySelector(`td[data-field="${this.uniqueFieldValue}"]`)
      
      // Check against existing database values
      if (this.existingValuesSet.has(uniqueValue)) {
        uniqueCell?.classList.add("cell-error")
        row.classList.add("row-duplicate-db")
        hasError = true
      } else {
        row.classList.remove("row-duplicate-db")
      }
      
      // Check against other rows in the import
      const allRows = Array.from(this.tableBodyTarget.querySelectorAll("tr"))
      const otherRowsWithSameValue = allRows.filter(r => {
        if (r === row) return false
        const cell = r.querySelector(`td[data-field="${this.uniqueFieldValue}"]`)
        return cell?.textContent.toLowerCase().trim() === uniqueValue
      })
      
      if (otherRowsWithSameValue.length > 0) {
        uniqueCell?.classList.add("cell-error")
        row.classList.add("row-duplicate-import")
        hasError = true
        
        // Mark the other duplicate rows too
        otherRowsWithSameValue.forEach(r => {
          r.classList.add("row-duplicate-import")
          const cell = r.querySelector(`td[data-field="${this.uniqueFieldValue}"]`)
          cell?.classList.add("cell-error")
        })
      } else {
        row.classList.remove("row-duplicate-import")
      }
    }
    
    // Set row error state
    if (hasError) {
      row.classList.add("row-error")
    } else {
      row.classList.remove("row-error")
    }
    
    return !hasError
  }

  validateAllRows() {
    const rows = this.tableBodyTarget.querySelectorAll("tr")
    rows.forEach(row => this.validateRow(row))
  }

  updateCounts() {
    const rows = Array.from(this.tableBodyTarget.querySelectorAll("tr"))
    const nonEmptyRows = rows.filter(row => {
      const cells = row.querySelectorAll("td[data-field]")
      return Array.from(cells).some(cell => cell.textContent.trim())
    })
    
    const errorRows = nonEmptyRows.filter(row => row.classList.contains("row-error"))
    const validRows = nonEmptyRows.filter(row => !row.classList.contains("row-error"))
    
    if (this.hasRowCountTarget) {
      this.rowCountTarget.textContent = nonEmptyRows.length
    }
    if (this.hasValidCountTarget) {
      this.validCountTarget.textContent = validRows.length
    }
    if (this.hasErrorCountTarget) {
      this.errorCountTarget.textContent = errorRows.length
    }
    
    // Enable/disable import button
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = validRows.length === 0
    }
  }

  // File input handling
  selectFile(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return
    
    const formData = new FormData()
    formData.append("file", file)
    
    // Get the parse URL from the form action
    const form = this.element.closest("form")
    const parseUrl = form.dataset.parseUrl
    
    fetch(parseUrl, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        toast.error(data.error)
        return
      }
      
      // Clear existing rows
      this.tableBodyTarget.innerHTML = ""
      this.seenValues.clear()
      
      // Add rows from file
      data.rows.forEach(rowData => {
        const values = this.columnsValue.map(col => rowData[col.field] || "")
        this.addRow(values)
      })
      
      // Add empty rows at end
      this.addEmptyRows(3)
      
      this.validateAllRows()
      this.updateCounts()
    })
    .catch(error => {
      console.error("Error parsing file:", error)
      toast.error("Error al procesar el archivo")
    })
    
    // Reset file input
    this.fileInputTarget.value = ""
  }

  // Form submission
  submitImport(event) {
    event.preventDefault()
    
    this.validateAllRows()
    
    const rows = this.getRowsData()
    const validRows = rows.filter(r => !r._hasError)
    
    if (validRows.length === 0) {
      toast.warning("No hay filas válidas para importar")
      return
    }
    
    // Confirm import
    if (!confirm(`¿Importar ${validRows.length} registros?`)) {
      return
    }
    
    // Prepare form data
    const form = this.element.closest("form")
    const formData = new FormData(form)
    formData.set("rows_data", JSON.stringify(validRows))
    
    // Disable button and show progress
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = true
      this.importButtonTarget.textContent = "Importando..."
    }
    
    fetch(form.action, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.async) {
        // Background job started, begin polling
        this.importTaskIdValue = data.import_task_id
        this.showProgress()
        this.startPolling()
      } else {
        // Synchronous import completed
        this.handleImportComplete(data)
      }
    })
    .catch(error => {
      console.error("Import error:", error)
      toast.error("Error al importar: " + error.message)
      this.resetImportButton()
    })
  }

  getRowsData() {
    const rows = Array.from(this.tableBodyTarget.querySelectorAll("tr"))
    
    return rows.map(row => {
      const cells = row.querySelectorAll("td[data-field]")
      const rowData = { _hasError: row.classList.contains("row-error") }
      const sizesData = {}
      
      cells.forEach(cell => {
        const field = cell.dataset.field
        const value = cell.textContent.trim()
        if (value) {
          rowData[field] = value
          
          // If this is a size field, also collect it for _sizes
          if (field.startsWith("size_")) {
            // Convert field name back to size: "size_18h" -> "18.5", "size_12_13" -> "12/13"
            let size = field.replace("size_", "")
            size = size.replace("h", ".5").replace("_", "/")
            const quantity = parseInt(value, 10)
            if (!isNaN(quantity) && quantity >= 0) {
              sizesData[size] = quantity
            }
          }
        }
      })
      
      // Add _sizes if we collected any size data
      if (Object.keys(sizesData).length > 0) {
        rowData["_sizes"] = sizesData
      }
      
      // Skip completely empty rows (ignore size fields for this check)
      const hasContent = Object.keys(rowData).some(k => 
        k !== "_hasError" && k !== "_sizes" && !k.startsWith("size_") && rowData[k]
      )
      if (!hasContent) {
        return null
      }
      
      return rowData
    }).filter(Boolean)
  }

  showProgress() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove("hidden")
    }
    this.tableTarget.classList.add("importing")
  }

  hideProgress() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add("hidden")
    }
    this.tableTarget.classList.remove("importing")
  }

  updateProgress(percentage, text = null) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }
    if (this.hasProgressTextTarget && text) {
      this.progressTextTarget.textContent = text
    }
  }

  startPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }
    
    this.pollTimer = setInterval(() => {
      this.checkImportStatus()
    }, this.pollIntervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  checkImportStatus() {
    if (!this.importTaskIdValue) return
    
    const form = this.element.closest("form")
    const statusUrl = form.dataset.statusUrl.replace("__ID__", this.importTaskIdValue)
    
    fetch(statusUrl, {
      headers: {
        "Accept": "application/json"
      }
    })
    .then(response => response.json())
    .then(data => {
      this.updateProgress(
        data.progress,
        `Procesando ${data.processed_rows} de ${data.total_rows} registros...`
      )
      
      if (data.status === "completed" || data.status === "failed") {
        this.stopPolling()
        this.handleImportComplete(data)
      }
    })
    .catch(error => {
      console.error("Status check error:", error)
      this.stopPolling()
    })
  }

  handleImportComplete(data) {
    this.hideProgress()
    this.resetImportButton()
    
    if (data.success) {
      this.showSuccessMessage(data)
    }
    
    if (data.errors && data.errors.length > 0) {
      this.showErrors(data.errors)
    }
    
    // Refresh the page after a short delay if successful
    if (data.success && (!data.errors || data.errors.length === 0)) {
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    }
  }

  showSuccessMessage(data) {
    const message = `Se importaron ${data.created_count} registros correctamente.`
    toast.success(message)
  }

  showErrors(errors) {
    if (this.hasErrorSummaryTarget) {
      this.errorSummaryTarget.classList.remove("hidden")
    }
    
    if (this.hasErrorListTarget) {
      this.errorListTarget.innerHTML = errors.map(error => `
        <li class="import-error">
          <span class="import-error__row">Fila ${error.row}:</span>
          <span class="import-error__field">${error.field}</span>
          <span class="import-error__message">${error.message}</span>
        </li>
      `).join("")
    }
  }

  resetImportButton() {
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = false
      this.importButtonTarget.textContent = "Importar"
    }
  }

  clearAll(event) {
    event.preventDefault()
    
    if (!confirm("¿Limpiar todos los datos?")) return
    
    this.tableBodyTarget.innerHTML = ""
    this.seenValues.clear()
    this.addEmptyRows(5)
    this.updateCounts()
    
    if (this.hasErrorSummaryTarget) {
      this.errorSummaryTarget.classList.add("hidden")
    }
  }
}
